import { useMemo } from 'react';
import {
  ArrowsCounterClockwise,
  Circle,
  DotsThree,
  PencilSimple,
  RectangleDashed,
  TrashSimple,
  Waves,
} from '@phosphor-icons/react';
import { Button, Dropdown, Flex, Table, Typography } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { ColorSwatch } from '@elements/ColorPicker';
import { MuteLink } from '@elements/Common';
import { COLOR } from '@styles/theme';
import { CommonUtility, ToteStatusColors } from '@utility/index';
import { ToteTag } from './common';

const { Text } = Typography;

export type ToteRecord = {
  id: string;
  toteNumber?: string;
  color?: string;
  type?: string;
  zone?: { code?: string; color?: string };
  dimensions?: { length?: number; height?: number; width?: number; weightLimit?: number };
  status?: string;
  waves?: { id?: string; waveNumber?: string }[];
};

type TotesTableProps = {
  data: ToteRecord[];
  loading: boolean;
  totalData: number;
  filter: { page: number; limit: number };
  pageChanged: (page: number) => void;
  filterChanged: (key: string, value: unknown) => void;
  selectedById: Record<string, ToteRecord>;
  onSelectRow: (item: ToteRecord, selected: boolean) => void;
  onSelectAllRows: (items: ToteRecord[], selected: boolean) => void;
  editTote: (item: ToteRecord) => void;
  onDeleteTote: (item: ToteRecord) => void;
  resetTote: (item: ToteRecord) => void;
};

function getRowActionItems(
  record: ToteRecord,
  handlers: Pick<TotesTableProps, 'editTote' | 'onDeleteTote' | 'resetTote'>,
): MenuProps['items'] {
  const items: MenuProps['items'] = [
    {
      key: 'edit',
      label: 'Edit',
      icon: <PencilSimple size={16} />,
      onClick: () => handlers.editTote(record),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <TrashSimple size={16} />,
      danger: true,
      onClick: () => handlers.onDeleteTote(record),
    },
  ];

  if (record.status !== 'open') {
    items.push({
      key: 'reset',
      label: 'Reset',
      icon: <ArrowsCounterClockwise size={16} />,
      danger: true,
      onClick: () => handlers.resetTote(record),
    });
  }

  return items;
}

export function TotesTable({
  data,
  loading,
  totalData,
  filter,
  pageChanged,
  filterChanged,
  selectedById,
  onSelectRow,
  onSelectAllRows,
  editTote,
  onDeleteTote,
  resetTote,
}: TotesTableProps) {
  const columns: ColumnsType<ToteRecord> = useMemo(
    () => [
      {
        title: 'ID',
        key: 'toteNumber',
        dataIndex: 'toteNumber',
        render: (_, record) => record.toteNumber,
      },
      {
        title: 'Color Coding',
        key: 'color',
        render: (_, record) =>
          record.color ? <ColorSwatch backgroundColor={record.color} /> : null,
      },
      {
        title: 'Type',
        key: 'type',
        render: (_, record) =>
          record.type ? CommonUtility.toTitleCase(record.type) : null,
      },
      {
        title: 'Assigned Zone',
        key: 'zone',
        render: (_, record) =>
          record.zone?.code ? (
            <ToteTag color={record.zone?.color}>
              <RectangleDashed size={14} style={{ marginRight: 4 }} />
              {record.zone.code}
            </ToteTag>
          ) : (
            <Text type="secondary">Not Set</Text>
          ),
      },
      {
        title: 'Size',
        key: 'size',
        render: (_, record) =>
          record.dimensions?.length ? (
            <span>
              {record.dimensions.length}
              <Text type="secondary" className="mx-1">
                x
              </Text>
              {record.dimensions.height}
              <Text type="secondary" className="mx-1">
                x
              </Text>
              {record.dimensions.width}
              <Text type="secondary" className="ml-1">
                in.
              </Text>
            </span>
          ) : (
            <Text type="secondary">Not Set</Text>
          ),
      },
      {
        title: 'Weight Limit',
        key: 'weight_limit',
        render: (_, record) =>
          record.dimensions?.weightLimit ? (
            record.dimensions.weightLimit
          ) : (
            <Text type="secondary">Not Set</Text>
          ),
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, record) => (
          <Flex align="center" gap={8} wrap="wrap">
            <ToteTag color={ToteStatusColors[record.status ?? '']}>
              <Circle weight="fill" size={10} style={{ marginRight: 4 }} />
              {CommonUtility.removeUnderscoreAndCapitalize(record.status ?? '')}
            </ToteTag>
            {record?.waves?.[0]?.waveNumber ? (
              <MuteLink
                to={`/app/fulfill/waves/${record.waves[0].id}`}
                className="text-nowrap d-flex align-items-center"
                target="_blank"
              >
                <Waves size={18} className="mr-1" />
                {record.waves[0].waveNumber}
              </MuteLink>
            ) : null}
          </Flex>
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 48,
        fixed: 'right',
        render: (_, record) => (
          <Dropdown
            menu={{
              items: getRowActionItems(record, {
                editTote,
                onDeleteTote,
                resetTote,
              }),
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<DotsThree size={18} weight="bold" />}
              aria-label="Row actions"
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [editTote, onDeleteTote, resetTote],
  );

  const selectedRowKeys = useMemo(() => Object.keys(selectedById), [selectedById]);

  const pagination: TablePaginationConfig = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50, 100],
    showTotal: total => `${total} totes`,
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <Table<ToteRecord>
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      size="middle"
      tableLayout="fixed"
      pagination={pagination}
      rowSelection={{
        selectedRowKeys,
        preserveSelectedRowKeys: true,
        onSelect: (record, selected) => onSelectRow(record, selected),
        onSelectAll: (selected, _rows, changeRows) =>
          onSelectAllRows(changeRows, selected),
      }}
      style={{
        background: COLOR.white,
        borderRadius: 6,
        border: `1px solid ${COLOR.border}`,
        overflow: 'hidden',
      }}
    />
  );
}
